import { useNavigate } from "react-router-dom";
import { ApiOperationItems } from "~enums/apiOperationItems";
import { useApiOperations } from "~hooks/useApiOperations.ts";
import { useExportEntities } from "~pages/Entities/EntitiesListPage/hooks/useExportEntities.ts";
import { useImportEntities } from "~pages/Entities/EntitiesListPage/hooks/useImportEntities.ts";
import type { CreateEntity, UpdateEntity } from "~pages/Entities/types/upsertEntity.ts";
import type { DefaultOperationSuccessResponse } from "~types/response.ts";
import { ensureDefaultOperationResponse, isDefaultOperationSucceeded } from "~utils/defaultOperationResponse.ts";
import { useCreateEntity } from "./useCreateEntity.ts";
import { useDeleteEntity } from "./useDeleteEntity.ts";
import { useUpdateEntity } from "./useUpdateEntity.ts";

type CreateEntityNavigationState = {
  search?: string;
  status?: string;
};

export const useEntityService = () => {
  const { mutateAsync: createEntityFn, isPending: isCreatePending } = useCreateEntity();
  const { mutateAsync: updateEntityFn, isPending: isUpdatePending } = useUpdateEntity();
  const { mutateAsync: deleteEntityFn, isPending: isDeletePending } = useDeleteEntity();
  const { mutateAsync: importEntitiesFn, isPending: isImportPending } = useImportEntities();
  const { mutateAsync: exportEntitiesFn, isPending: isExportPending } = useExportEntities();
  const navigate = useNavigate();

  const { create, update, remove, execute } = useApiOperations(ApiOperationItems.ENTITY);

  const createEntity = async (payload: CreateEntity, options?: { returnUrl?: string; navigationState?: CreateEntityNavigationState }) => {
    const createdEntity = await create(() => createEntityFn(payload), {
      successMessageId: "entity.add.success",
    });

    if (createdEntity && options?.returnUrl) {
      navigate(options.returnUrl, { state: options.navigationState });
    }

    return createdEntity;
  };

  const updateEntity = (entityId: string, payload: Omit<UpdateEntity, "entityId">) =>
    update(() => updateEntityFn({ ...payload, entityId }), {
      successMessageId: "entity.update.success",
    });

  const deleteEntity = async (entityId: string) =>
    Boolean(await remove(() => deleteEntityFn(entityId), { successMessageId: "entity.delete.success" }));

  const importEntities = async (fileName: string) =>
    ensureDefaultOperationResponse(
      await execute(() => importEntitiesFn(fileName), {
        successMessageId: "entities.import.started",
        isSucceeded: isDefaultOperationSucceeded,
      }),
      "Teams import was not started",
    );

  const exportEntities = async (fieldNames: Array<string>): Promise<DefaultOperationSuccessResponse> =>
    ensureDefaultOperationResponse(
      await execute(() => exportEntitiesFn(fieldNames), {
        successMessageId: "entities.export.started",
        isSucceeded: isDefaultOperationSucceeded,
      }),
      "Teams export was not started",
    );

  return {
    createEntity,
    isCreatePending,
    updateEntity,
    isUpdatePending,
    deleteEntity,
    isDeletePending,
    importEntities,
    isImportPending,
    exportEntities,
    isExportPending,
  };
};
